import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
var _excluded = ["labelStack", "time"],
  _excluded2 = ["stopTime", "labelStack"],
  _excluded3 = ["labelStack"],
  _excluded4 = ["labelStack"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import _regeneratorRuntime from "@babel/runtime/regenerator";
import Bowser from 'bowser-ultralight';
import { fg } from '@atlaskit/platform-feature-flags';
import { getLighthouseMetrics } from '../additional-payload';
import { CHRReporter } from '../assets';
import * as bundleEvalTiming from '../bundle-eval-timing';
import coinflip from '../coinflip';
import { getConfig, getExperimentalInteractionRate, getUfoNameOverrides } from '../config';
import { getExperimentalVCMetrics } from '../create-experimental-interaction-metrics-payload';
import { getBm3Timings } from '../custom-timings';
import { getGlobalErrorCount } from '../global-error-handler';
import { getPageVisibilityState } from '../hidden-timing';
import * as initialPageLoadExtraTiming from '../initial-page-load-extra-timing';
import { interactionSpans as atlaskitInteractionSpans } from '../interaction-metrics';
import * as resourceTiming from '../resource-timing';
import { filterResourceTimings } from '../resource-timing/common/utils/resource-timing-buffer';
import { roundEpsilon } from '../round-number';
import { getProfilerAsyncRuntime, getProfilerRuntimeByTag, getProfilerTotalRuntime, markProfilingEnd, markProfilingStart, resetProfilerMeasurements, withProfiling } from '../self-measurements';
import * as ssr from '../ssr';
import { buildSegmentTree, labelStackStartWith, optimizeLabelStack, sanitizeUfoName, stringifyLabelStackFully } from './common/utils';
import getInteractionStatus from './utils/get-interaction-status';
import getPageVisibilityUpToTTAI from './utils/get-page-visibility-up-to-ttai';
import { getReactUFOPayloadVersion } from './utils/get-react-ufo-payload-version';
import getSSRDoneTimeValue from './utils/get-ssr-done-time-value';
import getVCMetrics from './utils/get-vc-metrics';
var getUfoNameOverride = withProfiling(function getUfoNameOverride(interaction) {
  var ufoName = interaction.ufoName,
    apdex = interaction.apdex;
  try {
    var ufoNameOverrides = getUfoNameOverrides();
    if (ufoNameOverrides != null) {
      var metricKey = apdex.length > 0 ? apdex[0].key : '';
      if (ufoNameOverrides[ufoName][metricKey]) {
        return ufoNameOverrides[ufoName][metricKey];
      }
    }
    return ufoName;
  } catch (e) {
    return ufoName;
  }
});
var getEarliestLegacyStopTime = withProfiling(function getEarliestLegacyStopTime(interaction, labelStack) {
  var earliestLegacyStopTime = null;
  interaction.apdex.forEach(function (a) {
    var _a$labelStack;
    if (!(a !== null && a !== void 0 && a.stopTime)) {
      return;
    }
    if (!labelStackStartWith((_a$labelStack = a.labelStack) !== null && _a$labelStack !== void 0 ? _a$labelStack : [], labelStack)) {
      return;
    }
    if (a.stopTime > interaction.start && (earliestLegacyStopTime !== null && earliestLegacyStopTime !== void 0 ? earliestLegacyStopTime : a.stopTime) >= a.stopTime) {
      earliestLegacyStopTime = a.stopTime;
    }
  });
  return earliestLegacyStopTime;
});
var getBm3EndTimeOrFallbackValue = withProfiling(function getBm3EndTimeOrFallbackValue(interaction) {
  var _getEarliestLegacySto;
  var labelStack = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var fallbackValue = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : interaction.end;
  if (interaction.type === 'press') {
    return fallbackValue;
  }
  return (_getEarliestLegacySto = getEarliestLegacyStopTime(interaction, labelStack)) !== null && _getEarliestLegacySto !== void 0 ? _getEarliestLegacySto : fallbackValue;
});
var getPageVisibilityUpToTTI = withProfiling(function getPageVisibilityUpToTTI(interaction) {
  var start = interaction.start;
  var bm3EndTimeOrInteractionEndTime = getBm3EndTimeOrFallbackValue(interaction);
  return getPageVisibilityState(start, bm3EndTimeOrInteractionEndTime);
});
var getVisibilityStateFromPerformance = withProfiling(function getVisibilityStateFromPerformance(stop) {
  try {
    var results = performance.getEntriesByType('visibility-state');
    if (!results || results.length === 0) {
      return null;
    }
    return results.reduce(function () {
      var acc = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
      var _ref = arguments.length > 1 ? arguments[1] : undefined,
        name = _ref.name,
        startTime = _ref.startTime;
      if (startTime > stop) {
        return acc;
      }
      if (acc === null && name === null) {
        return null;
      }
      if (acc === null) {
        return name;
      }
      if (acc !== name) {
        return 'mixed';
      }
      return acc;
    }, null);
  } catch (e) {
    return null;
  }
});
var getMoreAccuratePageVisibilityUpToTTI = withProfiling(function getMoreAccuratePageVisibilityUpToTTI(interaction) {
  var old = getPageVisibilityUpToTTI(interaction);
  var tti = getEarliestLegacyStopTime(interaction, []);
  if (!tti) {
    return old;
  }
  var buffered = getVisibilityStateFromPerformance(tti);
  if (!buffered) {
    return old;
  }
  if (buffered !== old) {
    return 'mixed';
  }
  return old;
});
var getMoreAccuratePageVisibilityUpToTTAI = withProfiling(function getMoreAccuratePageVisibilityUpToTTAI(interaction) {
  var old = getPageVisibilityUpToTTAI(interaction);
  var buffered = getVisibilityStateFromPerformance(interaction.end);
  if (!buffered) {
    return old;
  }
  if (buffered !== old) {
    return 'mixed';
  }
  return old;
});
var getResourceTimings = withProfiling(function getResourceTimings(start, end) {
  var _resourceTiming$getRe;
  return (_resourceTiming$getRe = resourceTiming.getResourceTimings(start, end)) !== null && _resourceTiming$getRe !== void 0 ? _resourceTiming$getRe : undefined;
});
var getBundleEvalTimings = withProfiling(function getBundleEvalTimings(start) {
  return bundleEvalTiming.getBundleEvalTimings(start);
});
var getSSRSuccess = withProfiling(function getSSRSuccess(type) {
  return type === 'page_load' ? ssr.getSSRSuccess() : undefined;
});
var getSSRFeatureFlags = withProfiling(function getSSRFeatureFlags(type) {
  return type === 'page_load' ? ssr.getSSRFeatureFlags() : undefined;
});
var getLCP = withProfiling(function getLCP(end) {
  return new Promise(function (resolve) {
    var observer;
    var timeout = setTimeout(function () {
      var _observer;
      (_observer = observer) === null || _observer === void 0 || _observer.disconnect();
      resolve(null);
    }, 200);
    var performanceObserverCallback = withProfiling(function performanceObserverCallback(list) {
      var entries = Array.from(list.getEntries());
      var lastEntry = entries.reduce(function (agg, entry) {
        // Use the latest LCP candidate before TTAI
        if (entry.startTime <= end && (agg === null || agg.startTime < entry.startTime)) {
          return entry;
        }
        return agg;
      }, null);
      clearTimeout(timeout);
      if (!lastEntry || lastEntry === null) {
        resolve(null);
      } else {
        resolve(lastEntry.startTime);
      }
    });
    observer = new PerformanceObserver(performanceObserverCallback);
    observer.observe({
      type: 'largest-contentful-paint',
      buffered: true
    });
  });
});
var getPaintMetrics = withProfiling( /*#__PURE__*/function () {
  var _getPaintMetrics = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(type, end) {
    var metrics, lcp;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          if (!(type !== 'page_load')) {
            _context.next = 2;
            break;
          }
          return _context.abrupt("return", {});
        case 2:
          metrics = {};
          performance.getEntriesByType('paint').forEach(function (entry) {
            if (entry.name === 'first-paint') {
              metrics['metric:fp'] = Math.round(entry.startTime);
            }
            if (entry.name === 'first-contentful-paint') {
              metrics['metric:fcp'] = Math.round(entry.startTime);
            }
          });
          _context.next = 6;
          return getLCP(end);
        case 6:
          lcp = _context.sent;
          if (lcp) {
            metrics['metric:lcp'] = Math.round(lcp);
          }
          return _context.abrupt("return", metrics);
        case 9:
        case "end":
          return _context.stop();
      }
    }, _callee);
  }));
  function getPaintMetrics(_x, _x2) {
    return _getPaintMetrics.apply(this, arguments);
  }
  return getPaintMetrics;
}());
var getTTAI = withProfiling(function getTTAI(interaction) {
  var start = interaction.start,
    end = interaction.end;
  var pageVisibilityUpToTTAI = getPageVisibilityUpToTTAI(interaction);
  return !interaction.abortReason && pageVisibilityUpToTTAI === 'visible' ? Math.round(end - start) : undefined;
});
var getNavigationMetrics = withProfiling(function getNavigationMetrics(type) {
  if (type !== 'page_load') {
    return {};
  }
  var entries = performance.getEntriesByType('navigation');
  if (entries.length === 0) {
    return {};
  }
  var navigation = entries[0];
  var metrics = {
    // From https://www.w3.org/TR/resource-timing/
    redirectStart: Math.round(navigation.redirectStart),
    redirectEnd: Math.round(navigation.redirectEnd),
    fetchStart: Math.round(navigation.fetchStart),
    domainLookupStart: Math.round(navigation.domainLookupStart),
    domainLookupEnd: Math.round(navigation.domainLookupEnd),
    connectStart: Math.round(navigation.connectStart),
    connectEnd: Math.round(navigation.connectEnd),
    secureConnectionStart: Math.round(navigation.secureConnectionStart),
    requestStart: Math.round(navigation.requestStart),
    responseStart: Math.round(navigation.responseStart),
    responseEnd: Math.round(navigation.responseEnd),
    encodedBodySize: Math.round(navigation.encodedBodySize),
    decodedBodySize: Math.round(navigation.decodedBodySize),
    transferSize: Math.round(navigation.transferSize),
    // From https://www.w3.org/TR/navigation-timing-2/
    redirectCount: navigation.redirectCount,
    type: navigation.type,
    unloadEventEnd: Math.round(navigation.unloadEventEnd),
    unloadEventStart: Math.round(navigation.unloadEventStart),
    workerStart: Math.round(navigation.workerStart),
    nextHopProtocol: navigation.nextHopProtocol

    // The following properties are ignored because they provided limited value on a modern stack (e.g. the content
    // is usually rendered and interactive before the dom is fully parsed, dont't play well with streamed content...)
    //   * domComplete
    //   * domContentLoadedEventEnd
    //   * domContentLoadedEventStart
    //   * domInteractive
    //   * loadEventEnd
    //   * loadEventStart
  };
  return {
    'metrics:navigation': metrics
  };
});
var getPPSMetrics = withProfiling(function getPPSMetrics(interaction) {
  var _interaction$apdex;
  var start = interaction.start,
    end = interaction.end;
  var config = getConfig();
  var interactionStatus = getInteractionStatus(interaction);
  var pageVisibilityUpToTTAI = getPageVisibilityUpToTTAI(interaction);
  var tti = (_interaction$apdex = interaction.apdex) === null || _interaction$apdex === void 0 || (_interaction$apdex = _interaction$apdex[0]) === null || _interaction$apdex === void 0 ? void 0 : _interaction$apdex.stopTime;
  var ttai = interactionStatus.originalInteractionStatus === 'SUCCEEDED' && pageVisibilityUpToTTAI === 'visible' ? Math.round(end - start) : undefined;
  var PPSMetricsAtTTI = tti !== undefined ? getLighthouseMetrics({
    start: start,
    stop: tti
  }) : null;
  var PPSMetricsAtTTAI = ttai !== undefined ? getLighthouseMetrics({
    start: start,
    stop: interaction.end
  }) : null;
  if (config !== null && config !== void 0 && config.shouldCalculateLighthouseMetricsFromTTAI && PPSMetricsAtTTAI !== null) {
    return PPSMetricsAtTTAI;
  }
  if (PPSMetricsAtTTI !== null) {
    return _objectSpread(_objectSpread({}, PPSMetricsAtTTI), {}, {
      'metrics@ttai': PPSMetricsAtTTAI
    });
  }
  return {};
});
var getSSRProperties = withProfiling(function getSSRProperties(type) {
  return {
    'ssr:success': getSSRSuccess(type),
    'ssr:featureFlags': getSSRFeatureFlags(type)
  };
});
var getAssetsMetrics = withProfiling(function getAssetsMetrics(interaction, SSRDoneTime) {
  try {
    var config = getConfig();
    var type = interaction.type;
    var allowedTypes = ['page_load'];
    var assetsConfig = config === null || config === void 0 ? void 0 : config.assetsConfig;
    if (!allowedTypes.includes(type) || !assetsConfig) {
      // Skip if: type not allowed or assetsClassification isn't configured
      return {};
    }
    var reporter = new CHRReporter();
    var resourceTimings = filterResourceTimings(interaction.start, interaction.end);
    var assets = reporter.get(resourceTimings, assetsConfig, SSRDoneTime);
    if (assets) {
      // Only add assets in case it exists
      return {
        'event:assets': assets
      };
    }
    return {};
  } catch (error) {
    // Skip CHR in case of error
    return {};
  }
});
var getBrowserMetadata = withProfiling(function getBrowserMetadata() {
  var data = {};
  var now = new Date();
  data['event:localHour'] = now.getHours(); // returns the hours for this date according to local time
  data['event:localDayOfWeek'] = now.getDay(); // Sunday - Saturday : 0 - 6
  data['event:localTimezoneOffset'] = now.getTimezoneOffset(); // A number representing the difference, in minutes, between the date as evaluated in the UTC time zone and as evaluated in the local time zone.

  if (navigator.userAgent != null) {
    var browser = Bowser.getParser(navigator.userAgent);
    data['event:browser:name'] = browser.getBrowserName();
    data['event:browser:version'] = browser.getBrowserVersion();
  }
  if (navigator.hardwareConcurrency != null) {
    data['event:cpus'] = navigator.hardwareConcurrency;
  }
  if (navigator.deviceMemory != null) {
    data['event:memory'] = navigator.deviceMemory;
  }

  // eslint-disable-next-line compat/compat
  if (navigator.connection != null) {
    data['event:network:effectiveType'] = navigator.connection.effectiveType;
    data['event:network:rtt'] = navigator.connection.rtt;
    data['event:network:downlink'] = navigator.connection.downlink;
  }
  return data;
});
var getTracingContextData = withProfiling(function getTracingContextData(interaction) {
  var trace = interaction.trace,
    start = interaction.start;
  var tracingContextData = {};
  if (trace) {
    tracingContextData = {
      'ufo:tracingContext': {
        'X-B3-TraceId': trace.traceId,
        'X-B3-SpanId': trace.spanId,
        // eslint-disable-next-line compat/compat
        browserTimeOrigin: +(performance.timeOrigin + start).toFixed(2)
      }
    };
  }
  return tracingContextData;
});
var optimizeCustomData = withProfiling(function optimizeCustomData(interaction) {
  var customData = interaction.customData,
    legacyMetrics = interaction.legacyMetrics;
  var customDataMap = customData.reduce(function (result, _ref2) {
    var _result$get$data, _result$get;
    var labelStack = _ref2.labelStack,
      data = _ref2.data;
    var label = stringifyLabelStackFully(labelStack);
    var value = (_result$get$data = (_result$get = result.get(label)) === null || _result$get === void 0 ? void 0 : _result$get.data) !== null && _result$get$data !== void 0 ? _result$get$data : {};
    result.set(label, {
      labelStack: optimizeLabelStack(labelStack, getReactUFOPayloadVersion(interaction.type)),
      data: Object.assign(value, data)
    });
    return result;
  }, new Map());
  if (legacyMetrics) {
    var legacyMetricsFiltered = legacyMetrics.filter(function (item) {
      return item.type === 'PAGE_LOAD';
    }).reduce(function (result, currentValue) {
      for (var _i = 0, _Object$entries = Object.entries(currentValue.custom || {}); _i < _Object$entries.length; _i++) {
        var _result$get$data2, _result$get2;
        var _Object$entries$_i = _slicedToArray(_Object$entries[_i], 2),
          key = _Object$entries$_i[0],
          value = _Object$entries$_i[1];
        var label = stringifyLabelStackFully([]);
        var labelValue = (_result$get$data2 = (_result$get2 = result.get(label)) === null || _result$get2 === void 0 ? void 0 : _result$get2.data) !== null && _result$get$data2 !== void 0 ? _result$get$data2 : {};
        result.set(label, {
          labelStack: optimizeLabelStack([], getReactUFOPayloadVersion(interaction.type)),
          data: Object.assign(labelValue, _defineProperty({}, key, value))
        });
      }
      return result;
    }, new Map());
    return [].concat(_toConsumableArray(customDataMap.values()), _toConsumableArray(legacyMetricsFiltered.values()));
  }
  return _toConsumableArray(customDataMap.values());
});
var optimizeReactProfilerTimings = withProfiling(function optimizeReactProfilerTimings(reactProfilerTimings, interactionStart, reactUFOVersion) {
  var reactProfilerTimingsMap = reactProfilerTimings.reduce(function (result, _ref3) {
    var labelStack = _ref3.labelStack,
      startTime = _ref3.startTime,
      commitTime = _ref3.commitTime,
      actualDuration = _ref3.actualDuration,
      type = _ref3.type;
    if (labelStack && startTime >= interactionStart) {
      var label = stringifyLabelStackFully(labelStack);
      var start = Math.round(startTime);
      var end = Math.round(commitTime);
      var timing = result.get(label) || {
        labelStack: optimizeLabelStack(labelStack, reactUFOVersion),
        startTime: start,
        endTime: end,
        mountCount: 0,
        rerenderCount: 0,
        renderDuration: 0
      };
      if (start < timing.startTime) {
        timing.startTime = start;
      }
      if (end > timing.endTime) {
        timing.endTime = end;
      }
      if (type === 'mount') {
        timing.mountCount += 1;
      }
      if (type === 'update') {
        timing.rerenderCount += 1;
      }
      timing.renderDuration += Math.round(actualDuration);
      result.set(label, timing);
    }
    return result;
  }, new Map());
  return _toConsumableArray(reactProfilerTimingsMap.values());
});
var optimizeRedirects = withProfiling(function optimizeRedirects(redirects, interactionStart) {
  var lastRedirectTime = interactionStart;
  var updatedRedirects = redirects.sort(function (a, b) {
    return a.time - b.time;
  }).reduce(function (result, redirect) {
    var fromInteractionName = redirect.fromInteractionName,
      time = redirect.time;
    if (lastRedirectTime >= interactionStart) {
      result.push({
        labelStack: [{
          n: fromInteractionName
        }],
        startTime: Math.round(lastRedirectTime),
        endTime: Math.round(time)
      });
    }
    lastRedirectTime = time;
    return result;
  }, []);
  return updatedRedirects;
});
var optimizeHoldInfo = withProfiling(function optimizeHoldInfo(holdInfo, interactionStart, reactUFOVersion) {
  var holdInfoMap = holdInfo.reduce(function (result, hold) {
    var labelStack = hold.labelStack,
      name = hold.name,
      start = hold.start,
      end = hold.end,
      ignoreOnSubmit = hold.ignoreOnSubmit;
    if (labelStack && !ignoreOnSubmit && start >= interactionStart) {
      var label = stringifyLabelStackFully([].concat(_toConsumableArray(labelStack), [{
        name: name
      }]));
      var startTime = Math.round(start);
      var endTime = Math.round(end);
      var timing = result.get(label) || {
        labelStack: optimizeLabelStack([].concat(_toConsumableArray(labelStack), [{
          name: name
        }]), reactUFOVersion),
        startTime: startTime,
        endTime: endTime
      };
      if (startTime < timing.startTime) {
        timing.startTime = startTime;
      }
      if (endTime > timing.endTime) {
        timing.endTime = endTime;
      }
      result.set(label, timing);
    }
    return result;
  }, new Map());
  return _toConsumableArray(holdInfoMap.values());
});
var optimizeSpans = withProfiling(function optimizeSpans(spans, interactionStart, reactUFOVersion) {
  var updatedSpans = spans.reduce(function (result, span) {
    var labelStack = span.labelStack,
      type = span.type,
      name = span.name,
      start = span.start,
      end = span.end;
    if (labelStack && start >= interactionStart) {
      result.push({
        labelStack: optimizeLabelStack([].concat(_toConsumableArray(labelStack), [{
          name: name
        }]), reactUFOVersion),
        startTime: Math.round(start),
        endTime: Math.round(end),
        type: type
      });
    }
    return result;
  }, []);
  return updatedSpans;
});
var optimizeRequestInfo = withProfiling(function optimizeRequestInfo(requestInfo, interactionStart, reactUFOVersion) {
  var updatedRequestInfo = requestInfo.reduce(function (result, reqInfo) {
    var labelStack = reqInfo.labelStack,
      name = reqInfo.name,
      start = reqInfo.start,
      end = reqInfo.end,
      networkStart = reqInfo.networkStart,
      networkComplete = reqInfo.networkComplete;
    var startTime = networkStart !== null && networkStart !== void 0 ? networkStart : start;
    var endTime = networkComplete !== null && networkComplete !== void 0 ? networkComplete : end;
    if (labelStack && start >= interactionStart && endTime) {
      result.push({
        labelStack: optimizeLabelStack([].concat(_toConsumableArray(labelStack), [{
          name: name
        }]), reactUFOVersion),
        startTime: Math.round(startTime),
        endTime: Math.round(endTime)
      });
    }
    return result;
  }, []);
  return updatedRequestInfo;
});
var optimizeCustomTimings = withProfiling(function optimizeCustomTimings(customTimings, interactionStart) {
  return customTimings.reduce(function (result, item) {
    Object.keys(item.data).forEach(function (key) {
      if (item.data[key].startTime >= interactionStart) {
        result.push({
          labelStack: [{
            n: key
          }],
          startTime: Math.round(item.data[key].startTime),
          endTime: Math.round(item.data[key].endTime)
        });
      }
    });
    return result;
  }, []);
});
var optimizeMarks = withProfiling(function optimizeMarks(marks, reactUFOVersion) {
  return marks.map(function (_ref4) {
    var labelStack = _ref4.labelStack,
      time = _ref4.time,
      others = _objectWithoutProperties(_ref4, _excluded);
    return _objectSpread(_objectSpread({}, others), {}, {
      labelStack: labelStack && optimizeLabelStack(labelStack, reactUFOVersion),
      time: Math.round(time)
    });
  });
});
var optimizeApdex = withProfiling(function optimizeApdex(apdex, reactUFOVersion) {
  return apdex.map(function (_ref5) {
    var stopTime = _ref5.stopTime,
      labelStack = _ref5.labelStack,
      others = _objectWithoutProperties(_ref5, _excluded2);
    return _objectSpread(_objectSpread({}, others), {}, {
      stopTime: Math.round(stopTime)
    }, labelStack ? {
      labelStack: optimizeLabelStack(labelStack, reactUFOVersion)
    } : {});
  });
});
var objectToArray = withProfiling(function objectToArray() {
  var obj = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Object.keys(obj).reduce(function (result, key) {
    result.push({
      label: key,
      data: obj[key]
    });
    return result;
  }, []);
});
var getBM3SubmetricsTimings = withProfiling(function getBM3SubmetricsTimings(submetrics) {
  if (!submetrics) {
    return null;
  }
  var submetricsTimings = submetrics.filter(function (item) {
    return typeof item.stop === 'number' && !!item.key && typeof item.start === 'number';
  }).map(function (item) {
    var childSubmetrics;
    var newKey = "include/".concat(item.key);
    if (item.submetrics) {
      childSubmetrics = getBM3SubmetricsTimings(item.submetrics);
    }
    return _objectSpread(_defineProperty({}, newKey, {
      endTime: item.stop - item.start,
      startTime: item.start
    }), childSubmetrics ? childSubmetrics : {});
  });
  return submetricsTimings;
});
var getBm3TrackerTimings = withProfiling(function getBm3TrackerTimings(interaction) {
  var interactionLegacyMetrics = interaction.legacyMetrics;
  if (!interactionLegacyMetrics) {
    return {};
  }
  var legacyMetrics = interactionLegacyMetrics.map(function (item) {
    var _item$config, _item$config2, _item$marks;
    return {
      key: item.key,
      startTime: item.start,
      stopTime: item.stop,
      type: (_item$config = item.config) === null || _item$config === void 0 ? void 0 : _item$config.type,
      reactUFOName: (_item$config2 = item.config) === null || _item$config2 === void 0 ? void 0 : _item$config2.reactUFOName,
      fmp: ((_item$marks = item.marks) === null || _item$marks === void 0 ? void 0 : _item$marks['fmp']) || item.stop,
      source: 'bm3',
      timings: getBm3Timings(item.marks, item.config.timings),
      submetrics: getBM3SubmetricsTimings(item.submetrics),
      pageVisibleState: item.pageVisibleState
    };
  }).filter(function (item) {
    return !!item.type;
  });
  return {
    legacyMetrics: legacyMetrics
  };
});
var getPayloadSize = withProfiling(function getPayloadSize(payload) {
  return Math.round(new TextEncoder().encode(JSON.stringify(payload)).length / 1024);
});
var getStylesheetMetrics = withProfiling(function getStylesheetMetrics() {
  // eslint-disable-next-line @atlaskit/platform/ensure-feature-flag-prefix
  if (!fg('ufo_capture_stylesheet_metrics')) {
    return {};
  }
  try {
    var stylesheets = Array.from(document.styleSheets);
    var stylesheetCount = stylesheets.length;
    var cssrules = Array.from(document.styleSheets).reduce(function (acc, item) {
      // Other domain stylesheets throw a SecurityError
      try {
        return acc + item.cssRules.length;
      } catch (e) {
        return acc;
      }
    }, 0);
    var styleElements = document.querySelectorAll('style').length;
    var styleProps = document.querySelectorAll('[style]');
    var styleDeclarations = Array.from(document.querySelectorAll('[style]')).reduce(function (acc, item) {
      try {
        if ('style' in item) {
          return acc + item.style.length;
        } else {
          return acc;
        }
      } catch (e) {
        return acc;
      }
    }, 0);
    return {
      'ufo:stylesheets': stylesheetCount,
      'ufo:styleElements': styleElements,
      'ufo:styleProps': styleProps.length,
      'ufo:styleDeclarations': styleDeclarations,
      'ufo:cssrules': cssrules
    };
  } catch (e) {
    return {};
  }
});
var regularTTAI;
var expTTAI;
var getErrorCounts = withProfiling(function getErrorCounts(interaction) {
  return {
    'ufo:errors:globalCount': getGlobalErrorCount(),
    'ufo:errors:count': interaction.errors.length
  };
});
function createInteractionMetricsPayload(_x3, _x4, _x5) {
  return _createInteractionMetricsPayload.apply(this, arguments);
}
function _createInteractionMetricsPayload() {
  _createInteractionMetricsPayload = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(interaction, interactionId, experimental) {
    var _window$location, _config$additionalPay;
    var operationTimer, interactionPayloadStart, config, end, start, ufoName, knownSegments, rate, type, abortReason, routeName, featureFlags, previousInteractionName, isPreviousInteractionAborted, abortedByInteractionName, pageVisibilityAtTTI, pageVisibilityAtTTAI, segments, segmentTree, isDetailedPayload, isPageLoad, calculatePageVisibilityFromTheStartOfPageLoad, moreAccuratePageVisibilityAtTTI, moreAccuratePageVisibilityAtTTAI, labelStack, getInitialPageLoadSSRMetrics, pageLoadInteractionMetrics, getDetailedInteractionMetrics, getPageLoadDetailedInteractionMetrics, newUFOName, resourceTimings, _yield$Promise$all, _yield$Promise$all2, vcMetrics, experimentalMetrics, paintMetrics, payload;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          operationTimer = markProfilingStart('createInteractionMetricsPayload');
          interactionPayloadStart = performance.now();
          config = getConfig();
          if (config) {
            _context3.next = 5;
            break;
          }
          throw Error('UFO Configuration not provided');
        case 5:
          end = interaction.end, start = interaction.start, ufoName = interaction.ufoName, knownSegments = interaction.knownSegments, rate = interaction.rate, type = interaction.type, abortReason = interaction.abortReason, routeName = interaction.routeName, featureFlags = interaction.featureFlags, previousInteractionName = interaction.previousInteractionName, isPreviousInteractionAborted = interaction.isPreviousInteractionAborted, abortedByInteractionName = interaction.abortedByInteractionName;
          pageVisibilityAtTTI = getPageVisibilityUpToTTI(interaction);
          pageVisibilityAtTTAI = getPageVisibilityUpToTTAI(interaction);
          segments = config.killswitchNestedSegments ? [] : knownSegments;
          segmentTree = getReactUFOPayloadVersion(interaction.type) === '2.0.0' ? buildSegmentTree(segments.map(function (segment) {
            return segment.labelStack;
          })) : {};
          isDetailedPayload = pageVisibilityAtTTAI === 'visible';
          isPageLoad = type === 'page_load';
          calculatePageVisibilityFromTheStartOfPageLoad = config.enableBetterPageVisibilityApi && isPageLoad;
          moreAccuratePageVisibilityAtTTI = calculatePageVisibilityFromTheStartOfPageLoad ? getMoreAccuratePageVisibilityUpToTTI(interaction) : null;
          moreAccuratePageVisibilityAtTTAI = calculatePageVisibilityFromTheStartOfPageLoad ? getMoreAccuratePageVisibilityUpToTTAI(interaction) : null;
          labelStack = interaction.labelStack ? {
            labelStack: optimizeLabelStack(interaction.labelStack, getReactUFOPayloadVersion(interaction.type))
          } : {}; // Page Load
          getInitialPageLoadSSRMetrics = function getInitialPageLoadSSRMetrics() {
            var _config$ssr;
            if (!isPageLoad) {
              return {};
            }
            var config = getConfig();
            var SSRDoneTimeValue = getSSRDoneTimeValue(config);
            var SSRDoneTime = SSRDoneTimeValue !== undefined ? {
              SSRDoneTime: Math.round(SSRDoneTimeValue)
            } : {};
            return _objectSpread(_objectSpread({}, SSRDoneTime), {}, {
              isBM3ConfigSSRDoneAsFmp: interaction.metaData.__legacy__bm3ConfigSSRDoneAsFmp,
              isUFOConfigSSRDoneAsFmp: interaction.metaData.__legacy__bm3ConfigSSRDoneAsFmp || !!(config !== null && config !== void 0 && (_config$ssr = config.ssr) !== null && _config$ssr !== void 0 && _config$ssr.getSSRDoneTime)
            });
          };
          pageLoadInteractionMetrics = getInitialPageLoadSSRMetrics(); // Detailed payload. Page visibility = visible
          getDetailedInteractionMetrics = function getDetailedInteractionMetrics(resourceTimings) {
            if (experimental || window.__UFO_COMPACT_PAYLOAD__ || !isDetailedPayload) {
              return {};
            }
            var spans = [].concat(_toConsumableArray(interaction.spans), _toConsumableArray(atlaskitInteractionSpans));
            atlaskitInteractionSpans.length = 0;
            return {
              errors: interaction.errors.map(function (_ref6) {
                var labelStack = _ref6.labelStack,
                  others = _objectWithoutProperties(_ref6, _excluded3);
                return _objectSpread(_objectSpread({}, others), {}, {
                  labelStack: labelStack && optimizeLabelStack(labelStack, getReactUFOPayloadVersion(interaction.type))
                });
              }),
              holdActive: _toConsumableArray(interaction.holdActive.values()),
              redirects: optimizeRedirects(interaction.redirects, start),
              holdInfo: optimizeHoldInfo(experimental ? interaction.holdExpInfo : interaction.holdInfo, start, getReactUFOPayloadVersion(interaction.type)),
              spans: optimizeSpans(spans, start, getReactUFOPayloadVersion(interaction.type)),
              requestInfo: optimizeRequestInfo(interaction.requestInfo, start, getReactUFOPayloadVersion(interaction.type)),
              customTimings: optimizeCustomTimings(interaction.customTimings, start),
              bundleEvalTimings: objectToArray(getBundleEvalTimings(start)),
              resourceTimings: objectToArray(resourceTimings)
            };
          }; // Page load & detailed payload
          getPageLoadDetailedInteractionMetrics = function getPageLoadDetailedInteractionMetrics() {
            var _config$ssr2;
            if (!isPageLoad || !isDetailedPayload) {
              return {};
            }
            var config = getConfig();
            return {
              initialPageLoadExtraTimings: objectToArray(initialPageLoadExtraTiming.getTimings()),
              SSRTimings: config !== null && config !== void 0 && (_config$ssr2 = config.ssr) !== null && _config$ssr2 !== void 0 && _config$ssr2.getSSRTimings ? config.ssr.getSSRTimings() : objectToArray(ssr.getSSRTimings())
            };
          };
          if (experimental) {
            expTTAI = getTTAI(interaction);
          } else {
            regularTTAI = getTTAI(interaction);
          }
          newUFOName = sanitizeUfoName(ufoName);
          resourceTimings = getResourceTimings(start, end);
          _context3.next = 25;
          return Promise.all([getVCMetrics(interaction), experimental ? getExperimentalVCMetrics(interaction) : Promise.resolve(undefined), getPaintMetrics(type, end)]);
        case 25:
          _yield$Promise$all = _context3.sent;
          _yield$Promise$all2 = _slicedToArray(_yield$Promise$all, 3);
          vcMetrics = _yield$Promise$all2[0];
          experimentalMetrics = _yield$Promise$all2[1];
          paintMetrics = _yield$Promise$all2[2];
          payload = {
            actionSubject: 'experience',
            action: 'measured',
            eventType: 'operational',
            source: 'measured',
            tags: ['observability'],
            attributes: {
              properties: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
                // basic
                'event:hostname': ((_window$location = window.location) === null || _window$location === void 0 ? void 0 : _window$location.hostname) || 'unknown',
                'event:product': config.product,
                'event:schema': '1.0.0',
                'event:sizeInKb': 0,
                'event:source': {
                  name: 'react-ufo/web',
                  version: getReactUFOPayloadVersion(interaction.type)
                },
                'event:region': config.region || 'unknown',
                'experience:key': experimental ? 'custom.experimental-interaction-metrics' : 'custom.interaction-metrics',
                'experience:name': newUFOName
              }, getBrowserMetadata()), getSSRProperties(type)), getAssetsMetrics(interaction, pageLoadInteractionMetrics === null || pageLoadInteractionMetrics === void 0 ? void 0 : pageLoadInteractionMetrics.SSRDoneTime)), getPPSMetrics(interaction)), paintMetrics), getNavigationMetrics(type)), vcMetrics), experimentalMetrics), (_config$additionalPay = config.additionalPayloadData) === null || _config$additionalPay === void 0 ? void 0 : _config$additionalPay.call(config, interaction)), getTracingContextData(interaction)), getStylesheetMetrics()), getErrorCounts(interaction)), {}, {
                interactionMetrics: _objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread(_objectSpread({
                  namePrefix: config.namePrefix || '',
                  segmentPrefix: config.segmentPrefix || '',
                  interactionId: interactionId,
                  pageVisibilityAtTTI: pageVisibilityAtTTI,
                  pageVisibilityAtTTAI: pageVisibilityAtTTAI,
                  experimental__pageVisibilityAtTTI: moreAccuratePageVisibilityAtTTI,
                  experimental__pageVisibilityAtTTAI: moreAccuratePageVisibilityAtTTAI,
                  // raw interaction metrics
                  rate: rate,
                  routeName: routeName,
                  type: type,
                  abortReason: abortReason,
                  featureFlags: featureFlags,
                  previousInteractionName: previousInteractionName,
                  isPreviousInteractionAborted: isPreviousInteractionAborted,
                  abortedByInteractionName: abortedByInteractionName,
                  // performance
                  apdex: optimizeApdex(interaction.apdex, getReactUFOPayloadVersion(interaction.type)),
                  end: Math.round(end),
                  start: Math.round(start),
                  segments: getReactUFOPayloadVersion(interaction.type) === '2.0.0' ? segmentTree : segments.map(function (_ref7) {
                    var labelStack = _ref7.labelStack,
                      others = _objectWithoutProperties(_ref7, _excluded4);
                    return _objectSpread(_objectSpread({}, others), {}, {
                      labelStack: optimizeLabelStack(labelStack, getReactUFOPayloadVersion(interaction.type))
                    });
                  }),
                  marks: optimizeMarks(interaction.marks, getReactUFOPayloadVersion(interaction.type)),
                  customData: optimizeCustomData(interaction),
                  reactProfilerTimings: optimizeReactProfilerTimings(interaction.reactProfilerTimings, start, getReactUFOPayloadVersion(interaction.type))
                }, labelStack), pageLoadInteractionMetrics), getDetailedInteractionMetrics(resourceTimings)), getPageLoadDetailedInteractionMetrics()), getBm3TrackerTimings(interaction)), {}, {
                  'metric:ttai': experimental ? regularTTAI || expTTAI : undefined,
                  'metric:experimental:ttai': expTTAI
                }),
                'ufo:payloadTime': roundEpsilon(performance.now() - interactionPayloadStart)
              }, fg('platform_ufo_self_timings') ? {
                'ufo:self:timings:total': getProfilerTotalRuntime(),
                'ufo:self:timings:async': getProfilerAsyncRuntime(),
                'ufo:self:timings:vc': getProfilerRuntimeByTag('vc')
              } : {})
            }
          };
          if (experimental) {
            regularTTAI = undefined;
            expTTAI = undefined;
          }
          markProfilingEnd(operationTimer);
          resetProfilerMeasurements();
          payload.attributes.properties['event:sizeInKb'] = getPayloadSize(payload.attributes.properties);
          return _context3.abrupt("return", payload);
        case 36:
        case "end":
          return _context3.stop();
      }
    }, _callee3);
  }));
  return _createInteractionMetricsPayload.apply(this, arguments);
}
export function createPayloads(_x6, _x7) {
  return _createPayloads.apply(this, arguments);
}
function _createPayloads() {
  _createPayloads = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(interactionId, interaction) {
    var ufoNameOverride, modifiedInteraction, interactionMetricsPayload;
    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) switch (_context4.prev = _context4.next) {
        case 0:
          ufoNameOverride = getUfoNameOverride(interaction);
          modifiedInteraction = _objectSpread(_objectSpread({}, interaction), {}, {
            ufoName: ufoNameOverride
          });
          _context4.next = 4;
          return createInteractionMetricsPayload(modifiedInteraction, interactionId);
        case 4:
          interactionMetricsPayload = _context4.sent;
          return _context4.abrupt("return", [interactionMetricsPayload]);
        case 6:
        case "end":
          return _context4.stop();
      }
    }, _callee4);
  }));
  return _createPayloads.apply(this, arguments);
}
export var createExperimentalMetricsPayload = withProfiling( /*#__PURE__*/function () {
  var _createExperimentalMetricsPayload = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(interactionId, interaction) {
    var config, ufoName, rate, pageVisibilityState, result;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          config = getConfig();
          if (config) {
            _context2.next = 3;
            break;
          }
          throw Error('UFO Configuration not provided');
        case 3:
          ufoName = sanitizeUfoName(interaction.ufoName);
          rate = getExperimentalInteractionRate(ufoName, interaction.type);
          if (coinflip(rate)) {
            _context2.next = 7;
            break;
          }
          return _context2.abrupt("return", null);
        case 7:
          pageVisibilityState = getPageVisibilityState(interaction.start, interaction.end);
          if (!(pageVisibilityState !== 'visible')) {
            _context2.next = 10;
            break;
          }
          return _context2.abrupt("return", null);
        case 10:
          _context2.next = 12;
          return createInteractionMetricsPayload(interaction, interactionId, true);
        case 12:
          result = _context2.sent;
          return _context2.abrupt("return", result);
        case 14:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  function createExperimentalMetricsPayload(_x8, _x9) {
    return _createExperimentalMetricsPayload.apply(this, arguments);
  }
  return createExperimentalMetricsPayload;
}());